import { create } from "zustand";
import type { CefrLevel } from "@/types/dialog";

export interface LanguagePreference {
  language: string;
  level?: CefrLevel;
}

const STORAGE_KEY = "glossika-language-prefs";

function loadFromStorage(): LanguagePreference[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as LanguagePreference[];
  } catch {
    return [];
  }
}

function saveToStorage(prefs: LanguagePreference[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

interface PreferencesState {
  preferences: LanguagePreference[];
  loaded: boolean;
  loadPreferences: () => void;
  setPreferences: (prefs: LanguagePreference[]) => void;
  addLanguage: (language: string, level?: CefrLevel) => void;
  removeLanguage: (language: string) => void;
  updateLevel: (language: string, level: CefrLevel) => void;
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  preferences: [],
  loaded: false,

  loadPreferences: () => {
    const prefs = loadFromStorage();
    set({ preferences: prefs, loaded: true });
  },

  setPreferences: (prefs) => {
    saveToStorage(prefs);
    set({ preferences: prefs });
  },

  addLanguage: (language, level) => {
    const { preferences } = get();
    if (preferences.some((p) => p.language === language)) return;
    const updated = [...preferences, { language, level }];
    saveToStorage(updated);
    set({ preferences: updated });
  },

  removeLanguage: (language) => {
    const updated = get().preferences.filter((p) => p.language !== language);
    saveToStorage(updated);
    set({ preferences: updated });
  },

  updateLevel: (language, level) => {
    const updated = get().preferences.map((p) =>
      p.language === language ? { ...p, level } : p,
    );
    saveToStorage(updated);
    set({ preferences: updated });
  },
}));
